import { useCallback, useEffect, useMemo, useState } from 'react';

import analyticsService from '../../../../services/analyticsService';
import getDateRange from '../../../../utils/getDateRange';

import type { IntentDistributionResponse, StudentIntentDistribution } from './types';

type UseIntentDistributionSectionProps = {
  allCourseIds: string[];
  classValue: string;
  groupCourseMap: Record<string, string[]>;
  timePeriodValue: string;
};

type UseIntentDistributionSectionReturn = {
  data: IntentDistributionResponse | null;
  error: string | null;
  loading: boolean;
  students: StudentIntentDistribution[];
};

function useIntentDistributionSection({
  allCourseIds,
  classValue,
  groupCourseMap,
  timePeriodValue,
}: UseIntentDistributionSectionProps): UseIntentDistributionSectionReturn {
  const [data, setData] = useState<IntentDistributionResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const currentCourseIds = useMemo(() => {
    return classValue === 'all' ? allCourseIds : groupCourseMap[classValue] || [];
  }, [classValue, allCourseIds, groupCourseMap]);

  const { startDate: currentStartDate, endDate: currentEndDate } = useMemo(() => {
    return getDateRange(timePeriodValue);
  }, [timePeriodValue]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result: IntentDistributionResponse = await analyticsService.getIntentDistribution({
        courseIds: currentCourseIds,
        startDate: currentStartDate,
        endDate: currentEndDate,
      });

      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [currentCourseIds, currentStartDate, currentEndDate]);

  useEffect(() => {
    if (allCourseIds.length > 0) {
      fetchData();
    } else {
      setLoading(false);
      setData(null);
    }
  }, [fetchData, allCourseIds.length]);

  const students = useMemo(() => {
    return data?.students || [];
  }, [data]);

  return {
    data,
    error,
    loading,
    students,
  };
}

export default useIntentDistributionSection;
